import Component from '@glimmer/component';
import { action } from '@ember/object';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import type ExtendedRouterService from 'scaleflow/services/router';
import Tippy from 'scaleflow/components/tippy';
import BuildingOffice2 from 'scaleflow/components/tailwind-icons/building-office-2';
import Dropdown from 'scaleflow/components/molecules/dropdown';
import MenuActions from 'scaleflow/components/molecules/menu-actions';
import { getColorFromInitiativeTitle } from 'scaleflow/utils/colorClasses';
import { isModifiedClick } from 'scaleflow/utils/switch-tenant-url';

export interface ActiveOrganisationBadgeSignature {
  Args: object;
  Element: HTMLDivElement;
}

export default class ActiveOrganisationBadge extends Component<ActiveOrganisationBadgeSignature> {
  @service declare organisation: any;
  @service declare router: ExtendedRouterService;

  tippyInstance: any;

  get sortedOrganisationList() {
    return this.organisation.sortedOrganisationList;
  }

  get hasMultipleOrganisations() {
    return this.sortedOrganisationList.length > 1;
  }

  get badgeColorClass() {
    return getColorFromInitiativeTitle(this.organisation.id);
  }

  @action
  isCurrentOrganisation(organisationId: string): boolean {
    return this.organisation.isCurrentOrganisation(organisationId);
  }

  @action
  async handleOrgSwitch(organisation: any) {
    await this.router.clearAllQueryParams();
    this.organisation.switchOrganisation(organisation.id);
  }

  @action
  hrefForSwitch(organisation: any): string {
    return this.organisation.urlForSwitch(organisation.id);
  }

  // A plain click keeps today's behaviour: switch in this tab via the Ember
  // transition, no full navigation. Ctrl/cmd/shift/middle-click are left
  // alone so the browser opens `href` itself -- that's what lands a new tab
  // on the clicked tenant, since `initializers/switch-tenant.js` only reads
  // `?switchTenant=` off a fresh page load.
  @action
  handleAnchorClick(organisation: any, event: MouseEvent) {
    if (isModifiedClick(event)) return;
    event.preventDefault();
    void this.handleOrgSwitch(organisation);
  }

  @action
  onCreate(instance: any) {
    this.tippyInstance = instance;
  }

  <template>
    {{#if this.hasMultipleOrganisations}}
      <div class="fixed top-4 right-4 z-[200]" ...attributes>
        <button
          type="button"
          class="flex items-center gap-1.5 px-3 py-1.5 rounded-full shadow-md
            {{this.badgeColorClass}}"
          data-test-id="active-organisation-badge"
        >
          <BuildingOffice2 class="w-3.5 h-3.5 text-white shrink-0" />
          <span
            class="text-xs font-semibold text-white max-w-[200px] block overflow-hidden text-ellipsis whitespace-nowrap"
            title={{this.organisation.name}}
          >{{this.organisation.name}}</span>

          <Tippy
            @interactive="true"
            @trigger="click"
            @placement="bottom-end"
            @onCreate={{this.onCreate}}
          >
            <Dropdown>
              <div class="max-h-[400px] overflow-y-auto">
                {{#each this.sortedOrganisationList as |org|}}
                  {{#if (this.isCurrentOrganisation org.id)}}
                    <MenuActions
                      @title={{org.name}}
                      @icon="check"
                      @iconClasses="text-green-600"
                      disabled={{true}}
                      @labelClass="inline-block overflow-hidden text-ellipsis"
                    />
                  {{else}}
                    <li class="w-full list-none">
                      {{! template-lint-disable no-nested-interactive }}
                      {{! This <a> is lexically inside the toggle <button>,
                        but Tippy's content div is detached and moved under
                        document.body as soon as the modifier installs
                        (tippy.js's default `appendTo`) -- it is never
                        actually a descendant of the button in the live DOM. }}
                      <a
                        href={{this.hrefForSwitch org}}
                        class="flex w-full items-center p-2 space-x-2 text-sm rounded transition text-gray-700 hover:bg-gray-100 whitespace-nowrap"
                        data-test-id="dropdown-item"
                        {{on "click" (fn this.handleAnchorClick org)}}
                      >
                        <span
                          class="inline-block overflow-hidden text-ellipsis"
                          data-test-id="label"
                        >{{org.name}}</span>
                      </a>
                      {{! template-lint-enable no-nested-interactive }}
                    </li>
                  {{/if}}
                {{/each}}
              </div>
            </Dropdown>
          </Tippy>
        </button>
      </div>
    {{/if}}
  </template>
}
